import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useGetServices } from '@/hooks/service/useGetServices'
import { formatDurations } from '@/utils/helpers/formatHelper'
import { formatMoneyRMG } from '@/utils/helpers/inputHelper'
import { ArrowLeftOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Drawer,
  DrawerProps,
  Input,
  Row,
  Skeleton,
  Typography,
} from 'antd'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import NoFound from '@/assets/illustrations/Searching-For-Answers.svg?react'
import { useTranslation } from 'react-i18next'
import { AppDrawer } from '../appDrawer/appDrawer'
const { Paragraph, Text, Title } = Typography
interface ServicesDrawerProps extends DrawerProps {
  selectedServicesVariant: { serviceId: string; variantId: string }[]
  onApply: (prop: { serviceId: string; variantId: string }[]) => void
}
interface ServiceVariant {
  serviceId: string
  variantId: string
  name: string
  duration: number
  price: number
}
const ServiceItems = ({
  data,
  onChange,
  checked,
}: {
  data: ServiceVariant
  onChange: (prop: CheckboxChangeEvent) => void
  checked: boolean
}) => {
  return (
    <label className="cursor-pointer" htmlFor={data.variantId}>
      <Card size="small" className="mb-4">
        <div className="flex">
          <Checkbox
            id={data.variantId}
            onChange={onChange}
            checked={checked}
            name={`${data.serviceId}|${data.variantId}`}
          />
          <div className="flex-1 ml-4 ">
            <div className="flex justify-between">
              <div className="flex-1">
                <Paragraph
                  style={{ marginBottom: 4 }}
                  className="font-semibold"
                >
                  {data.name}
                </Paragraph>
                <Text type="secondary">{formatDurations(data.duration)}</Text>
              </div>
              <Text className="text-base font-semibold">
                {formatMoneyRMG(data.price, 'Rp')}
              </Text>
            </div>
          </div>
        </div>
      </Card>
    </label>
  )
}

export const ServicesVariantPickerDrawer = ({
  selectedServicesVariant,
  onApply,
  ...props
}: ServicesDrawerProps) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<
    { serviceId: string; variantId: string }[]
  >([])

  const { data, isLoading } = useGetServices({ branch: '*', params: {} })
  const formatedData = useMemo(() => {
    const formated = data?.reduce((acc, service) => {
      const mapVariant = service.variants?.map((variant) => {
        return {
          serviceId: service.id,
          variantId: variant.id,
          duration: variant.duration || 0,
          name: `${service.name}${variant.name ? ` - ${variant.name}` : ''}`,
          price: variant.price,
        } as ServiceVariant
      })
      return [...acc, ...mapVariant]
    }, [] as ServiceVariant[])
    return formated
  }, [data])
  const [keyword, setKeyword] = useState('')
  const handleChange = (prop: CheckboxChangeEvent) => {
    const {
      target: { checked, name },
    } = prop

    const safeName = name || ''
    const [serviceId, variantId] = safeName.split('|')
    if (checked) {
      setSelected([...selected, { serviceId, variantId }])
      return
    }

    setSelected(selected.filter((el) => el.variantId !== variantId))
  }

  useEffect(() => {
    if (props.open) {
      setKeyword('')
      setSelected(selectedServicesVariant)
    }
  }, [props.open, selectedServicesVariant, data])
  const handleApply = () => {
    const payload = selected
    onApply(payload)
  }
  const handleChangeKeyword = (prop: ChangeEvent<HTMLInputElement>) => {
    setKeyword(prop.target.value)
  }
  const servicesFiltered = useMemo(() => {
    if (keyword) {
      return formatedData?.filter((el) =>
        el.name.toLowerCase().includes(keyword.toLowerCase()),
      )
    }
    return formatedData || []
  }, [formatedData, keyword])

  return (
    <AppDrawer
      size={500}
      closeIcon={<ArrowLeftOutlined />}
      title={t('common.select.services')}
      footer={
        <>
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <Button type="primary" ghost block onClick={props.onClose}>
                {t('common.actions.cancel')}
              </Button>
            </Col>
            <Col span={12}>
              <Button type="primary" block onClick={handleApply}>
                {t('common.actions.apply')}
              </Button>
            </Col>
          </Row>
        </>
      }
      {...props}
    >
      <Input.Search
        value={keyword}
        onChange={handleChangeKeyword}
        placeholder={t('common.search.services')}
      />
      <Divider className="border-stone-300" />
      {isLoading && (
        <>
          <Skeleton.Input block style={{ height: 72, marginBottom: 16 }} />
          <Skeleton.Input block style={{ height: 72, marginBottom: 16 }} />
        </>
      )}

      {servicesFiltered?.map((el) => (
        <ServiceItems
          checked={!!selected.find((slc) => slc.variantId === el.variantId)}
          onChange={handleChange}
          key={el.variantId}
          data={el}
        />
      ))}
      {!isLoading && !servicesFiltered?.length && keyword && (
        <div className="flex flex-col items-center">
          <NoFound className="w-[150px] h-[150px] text-pallet3" />
          <Title level={5}>{t('common.service.notFound')}</Title>
          <Text type="secondary" className="text-center">
            {t('common.service.notFound.desc')}
          </Text>
        </div>
      )}
    </AppDrawer>
  )
}
